// BackgroundSettingsDialog.tsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Slider,
  Typography,
  IconButton,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  TextField,
  Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Upload, ZoomIn, Close } from "@mui/icons-material";
import { THEME_COLORS } from "../../context/constants";
import { Actor, Asset } from "../../context/types";
import { uploadBackgroundAsset, getAssetsByWorkspace } from "../../client/api";
import { useNotification } from "../../context/NotificationContext";
import { useUserContext } from "../../context/UserContext";
import { removeBackground } from "@imgly/background-removal";
import { canUseFeatures } from "../../utils/utils";
import { WhiteTextField } from "../WhiteTextField";

interface BackgroundSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  actorId: string;
  workspaceId: string;
  videoPageId: string;
  currentScale?: number;
  currentBackground?: Asset | null;
  dimension: { width: number; height: number };
  onSave: (settings: {
    scale: number;
    backgroundAssetId?: string;
    selectedDimension?: { width: number; height: number };
    selectedAvatarOffset?: { x: number; y: number };
    backgroundFit?: "cover" | "crop" | "contain" | "none";
    backgroundPlayStyle?: "fit_to_scene" | "freeze" | "loop" | "once";
  }) => void;
  actor: Actor;
}

const BackgroundSettingsDialog: React.FC<BackgroundSettingsDialogProps> = ({
  open,
  onClose,
  actorId,
  workspaceId,
  videoPageId,
  currentScale = 1.0,
  currentBackground = null,
  dimension,
  onSave,
  actor,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [scale, setScale] = useState(currentScale);
  const [selectedBackground, setSelectedBackground] = useState<Asset | null>(
    currentBackground
  );
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [processedAvatar, setProcessedAvatar] = useState<string>("");
  const processingRef = useRef(false);
  const { user } = useUserContext();
  const { addNotification } = useNotification();
  const [currentDimension, setCurrentDimension] = useState(dimension);
  const [currentAvatarOffset, setCurrentAvatarOffset] = useState(
    actor.avatarOffset || { x: 0, y: 0 }
  );
  const [backgroundFit, setBackgroundFit] = useState<
    "cover" | "crop" | "contain" | "none"
  >(currentBackground?.fit || "cover");
  const [backgroundPlayStyle, setBackgroundPlayStyle] = useState<
    "fit_to_scene" | "freeze" | "loop" | "once"
  >(currentBackground?.playStyle || "loop");

  const PREVIEW_SCALE = 0.3;

  // Calculate transform for avatar preview
  const getAvatarTransform = () => {
    const previewWidth = currentDimension.width * PREVIEW_SCALE;
    const previewHeight = currentDimension.height * PREVIEW_SCALE;

    // Calculate the maximum possible movement based on avatar size
    // This matches HeyGen's behavior where the avatar can move outside the frame
    const maxMovementX = previewWidth * PREVIEW_SCALE * (1 + scale);
    const maxMovementY = previewHeight * PREVIEW_SCALE * (1 + scale);

    // Convert -1 to 1 range to pixel offsets
    const xOffset = currentAvatarOffset.x * maxMovementX;
    const yOffset = currentAvatarOffset.y * maxMovementY;

    return `translate(${xOffset}px, ${yOffset}px)`;
  };

  useEffect(() => {
    if (open) {
      setScale(currentScale);
      setSelectedBackground(currentBackground);
      setCurrentAvatarOffset(actor.avatarOffset || { x: 0, y: 0 });
      setBackgroundFit(currentBackground?.fit || "cover");
      setBackgroundPlayStyle(currentBackground?.playStyle || "loop");
      fetchAssets();
    }
  }, [open, currentScale, currentBackground, actor.avatarOffset]);

  const removeBackgrounds = async (imageUrl: string) => {
    let url1: string | null = null;
    let finalUrl: string | null = null;

    try {
      const blob1 = await removeBackground(imageUrl, {
        publicPath: "/bg-removal-assets/",
        debug: false,
        output: { format: "image/png", quality: 0.8 },
      });
      url1 = URL.createObjectURL(blob1);

      const blob2 = await removeBackground(url1, {
        publicPath: "/bg-removal-assets/",
        debug: false,
        model: "isnet",
        output: { format: "image/png", quality: 1.0 },
      });

      finalUrl = URL.createObjectURL(blob2);
      return finalUrl;
    } catch (error) {
      console.error("Background removal failed:", error);
      return imageUrl;
    } finally {
      if (url1) URL.revokeObjectURL(url1);
    }
  };

  useEffect(() => {
    const processAvatar = async () => {
      if (processingRef.current || !actor.preview_image_url) return;
      processingRef.current = true;

      try {
        const transparentAvatar = await removeBackgrounds(
          actor.preview_image_url
        );
        setProcessedAvatar(transparentAvatar);
      } catch (error) {
        console.error("Avatar processing failed:", error);
        setProcessedAvatar(actor.preview_image_url);
      } finally {
        processingRef.current = false;
      }
    };

    processAvatar();

    return () => {
      if (processedAvatar) URL.revokeObjectURL(processedAvatar);
    };
  }, [actor.preview_image_url]);

  const fetchAssets = async () => {
    setIsLoading(true);
    try {
      const assets = await getAssetsByWorkspace(workspaceId);
      setAssets(assets);
    } catch (error) {
      console.error("Failed to fetch assets:", error);
      addNotification("Failed to load background assets", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files?.[0]) return;

    setIsUploading(true);
    try {
      const file = event.target.files[0];
      const asset = await uploadBackgroundAsset(
        file,
        user?._id || "",
        workspaceId,
        undefined,
        videoPageId
      );
      setAssets((prev) => [asset, ...prev]);
      setSelectedBackground(asset);
      // Set default settings for new uploads
      setBackgroundFit("cover");
      setBackgroundPlayStyle("loop");
      addNotification("Background uploaded successfully", "success");
    } catch (error) {
      console.error("Failed to upload asset:", error);
      addNotification("Failed to upload background", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    onSave({
      scale,
      backgroundAssetId: selectedBackground?._id,
      selectedDimension: currentDimension,
      selectedAvatarOffset: currentAvatarOffset,
      backgroundFit,
      backgroundPlayStyle,
    });
    onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: THEME_COLORS.white,
          color: "black",
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Background Settings</Typography>
          <IconButton onClick={onClose} sx={{ color: "black" }}>
            <Close />
          </IconButton>
        </Box>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{
            "& .MuiTabs-indicator": {
              backgroundColor: THEME_COLORS.yellow,
            },
          }}
        >
          <Tab
            label="Background"
            sx={{
              color: activeTab === 0 ? THEME_COLORS.yellow : "black",
              "&:hover": {
                color: THEME_COLORS.yellow,
              },
            }}
          />
          <Tab
            label="Preview & Position"
            sx={{
              color: activeTab === 1 ? THEME_COLORS.yellow : "black",
              "&:hover": {
                color: THEME_COLORS.yellow,
              },
            }}
          />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {activeTab === 0 && (
          <Box>
            <Box mb={2}>
              <Button
                variant="contained"
                component="label"
                startIcon={<Upload />}
                disabled={isUploading}
                sx={{
                  backgroundColor: THEME_COLORS.yellow,
                  color: "black",
                  "&:hover": {
                    backgroundColor: THEME_COLORS.darkYellow,
                  },
                }}
              >
                {isUploading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Upload Background"
                )}
                <input
                  type="file"
                  hidden
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>

            {isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {assets.map((asset) => (
                  <Grid item xs={12} sm={6} md={4} key={asset._id}>
                    <Card
                      sx={{
                        border:
                          selectedBackground?._id === asset._id
                            ? `3px solid ${THEME_COLORS.yellow}`
                            : "none",
                      }}
                    >
                      <CardActionArea
                        onClick={() => setSelectedBackground(asset)}
                      >
                        {asset.type === "video" ? (
                          <video
                            src={asset.url}
                            style={{
                              width: "100%",
                              height: "140px",
                              objectFit: "cover",
                            }}
                            muted
                            loop
                            playsInline
                          />
                        ) : (
                          <CardMedia
                            component="img"
                            height="140"
                            image={asset.url}
                          />
                        )}
                        <Box p={1}>
                          <Typography noWrap>{asset.assetId}</Typography>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {activeTab === 1 && (
          <Box display="flex" gap={4}>
            {/* Settings Column */}
            <Box flex={1}>
              {/* Avatar Controls */}
              <Box mb={3}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Avatar Position
                </Typography>

                {/* Scale Control */}
                <Box mb={3}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">Scale (0.1-5.0)</Typography>
                    <Typography variant="body2" color={THEME_COLORS.yellow}>
                      {scale.toFixed(2)}
                    </Typography>
                  </Box>
                  <Slider
                    value={scale}
                    onChange={(_, value) => setScale(value as number)}
                    min={0.1}
                    max={5.0}
                    step={0.01}
                    marks={[
                      { value: 0.1, label: "0.1" },
                      { value: 1.0, label: "1.0" },
                      { value: 5.0, label: "5.0" },
                    ]}
                  />
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton
                      onClick={() => setScale(1.0)}
                      size="small"
                      title="Reset scale to 1.0"
                      sx={{ color: THEME_COLORS.yellow }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* X Offset Control */}
                <Box mb={3}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      X Position (-1.0-1.0)
                    </Typography>
                    <Typography variant="body2" color={THEME_COLORS.yellow}>
                      {currentAvatarOffset.x.toFixed(2)}
                    </Typography>
                  </Box>
                  <Slider
                    value={currentAvatarOffset.x}
                    onChange={(_, value) =>
                      setCurrentAvatarOffset((prev) => ({
                        ...prev,
                        x: value as number,
                      }))
                    }
                    min={-1.0}
                    max={1.0}
                    step={0.01}
                    marks={[
                      { value: -1.0, label: "-1" },
                      { value: 0, label: "0" },
                      { value: 1.0, label: "1" },
                    ]}
                  />
                </Box>

                {/* Y Offset Control */}
                <Box mb={3}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2">
                      Y Position (-1.0-1.0)
                    </Typography>
                    <Typography variant="body2" color={THEME_COLORS.yellow}>
                      {currentAvatarOffset.y.toFixed(2)}
                    </Typography>
                  </Box>
                  <Slider
                    value={currentAvatarOffset.y}
                    onChange={(_, value) =>
                      setCurrentAvatarOffset((prev) => ({
                        ...prev,
                        y: value as number,
                      }))
                    }
                    min={-1.0}
                    max={1.0}
                    step={0.01}
                    marks={[
                      { value: -1.0, label: "-1" },
                      { value: 0, label: "0" },
                      { value: 1.0, label: "1" },
                    ]}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Background Settings */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Background Settings
                </Typography>

                {/* Fit Settings */}
                <Box mb={3}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Background Fit
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {(["cover", "contain", "crop", "none"] as const).map(
                      (fitOption) => (
                        <Button
                          key={fitOption}
                          variant={
                            backgroundFit === fitOption
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => setBackgroundFit(fitOption)}
                          sx={{
                            textTransform: "capitalize",
                            color:
                              backgroundFit === fitOption
                                ? "black"
                                : THEME_COLORS.yellow,
                            backgroundColor:
                              backgroundFit === fitOption
                                ? THEME_COLORS.yellow
                                : "transparent",
                            borderColor: THEME_COLORS.yellow,
                            "&:hover": {
                              backgroundColor:
                                backgroundFit === fitOption
                                  ? THEME_COLORS.darkYellow
                                  : "rgba(255, 215, 0, 0.1)",
                              borderColor: THEME_COLORS.darkYellow,
                            },
                          }}
                        >
                          {fitOption}
                        </Button>
                      )
                    )}
                  </Box>
                </Box>

                {/* Play Style Settings */}
                {selectedBackground?.type === "video" && (
                  <Box mb={3}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Video Play Style
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {(
                        ["loop", "fit_to_scene", "freeze", "once"] as const
                      ).map((styleOption) => (
                        <Button
                          key={styleOption}
                          variant={
                            backgroundPlayStyle === styleOption
                              ? "contained"
                              : "outlined"
                          }
                          onClick={() => setBackgroundPlayStyle(styleOption)}
                          sx={{
                            textTransform: "capitalize",
                            color:
                              backgroundPlayStyle === styleOption
                                ? "black"
                                : THEME_COLORS.yellow,
                            backgroundColor:
                              backgroundPlayStyle === styleOption
                                ? THEME_COLORS.yellow
                                : "transparent",
                            borderColor: THEME_COLORS.yellow,
                            "&:hover": {
                              backgroundColor:
                                backgroundPlayStyle === styleOption
                                  ? THEME_COLORS.darkYellow
                                  : "rgba(255, 215, 0, 0.1)",
                              borderColor: THEME_COLORS.darkYellow,
                            },
                          }}
                        >
                          {styleOption.replace("_", " ")}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Preview Column - Preserving original scaling calculations */}
            <Box width={currentDimension.width * 0.3 + 40}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Preview
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  width: `${currentDimension.width * PREVIEW_SCALE}px`,
                  height: `${currentDimension.height * PREVIEW_SCALE}px`,
                  backgroundColor: selectedBackground
                    ? "transparent"
                    : THEME_COLORS.darkGray,
                  backgroundImage:
                    selectedBackground?.type === "image"
                      ? `url(${selectedBackground.url})`
                      : "none",
                  backgroundSize: backgroundFit,
                  backgroundPosition: "center",
                  overflow: "hidden",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                }}
              >
                {/* Grid Lines - Original implementation */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "none",
                  }}
                >
                  {/* Vertical Lines */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "25%",
                      width: "1px",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      width: "1px",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      left: "75%",
                      width: "1px",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />

                  {/* Horizontal Lines */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "25%",
                      width: "100%",
                      height: "1px",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      width: "100%",
                      height: "1px",
                      backgroundColor: "rgba(255, 255, 255, 0.5)",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "75%",
                      width: "100%",
                      height: "1px",
                      backgroundColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />

                  {/* Coordinate Labels */}
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    -1
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    0
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      right: "5px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    1
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "50%",
                      top: "5px",
                      transform: "translateX(-50%)",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    -1
                  </Typography>
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "50%",
                      bottom: "5px",
                      transform: "translateX(-50%)",
                      fontSize: "12px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    1
                  </Typography>
                </Box>

                {selectedBackground?.type === "video" && (
                  <video
                    src={selectedBackground.url}
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      objectFit:
                        backgroundFit === "crop" ? "cover" : backgroundFit,
                      objectPosition: "center",
                    }}
                    autoPlay
                    loop={backgroundPlayStyle === "loop"}
                    muted
                    playsInline
                  />
                )}

                {processedAvatar && (
                  <Box
                    component="img"
                    src={processedAvatar}
                    sx={{
                      width: `${
                        currentDimension.width * PREVIEW_SCALE * scale
                      }px`,
                      height: `${
                        currentDimension.height * PREVIEW_SCALE * scale
                      }px`,
                      objectFit: "contain",
                      position: "absolute",
                      left: "50%",
                      top: "50%",
                      zIndex: 1,
                      filter: "drop-shadow(0 0 8px rgba(0,0,0,0.3))",
                      transform: `${getAvatarTransform()} translate(-50%, -50%)`,
                      transition: "transform 0.1s ease",
                      transformOrigin: "center center",
                    }}
                    alt="Avatar preview"
                  />
                )}

                {/* Position Info */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "10px",
                    left: "10px",
                    backgroundColor: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                  }}
                >
                  Position: ({currentAvatarOffset.x.toFixed(2)},{" "}
                  {currentAvatarOffset.y.toFixed(2)})<br />
                  Scale: {scale.toFixed(2)}
                </Box>
              </Box>
              {/* Video Dimension Controls */}
              <Box mb={3} mt={5}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Video Dimensions
                </Typography>
                <Box display="flex" gap={2} alignItems="center">
                  <WhiteTextField
                    label="Width"
                    type="number"
                    value={currentDimension.width}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setCurrentDimension((prev) => ({ ...prev, width: 0 }));
                        return;
                      }
                      const width = parseInt(value, 10);
                      if (!isNaN(width)) {
                        setCurrentDimension((prev) => ({ ...prev, width }));
                      }
                    }}
                    onBlur={() => {
                      if (currentDimension.width % 2 !== 0) {
                        const evenWidth = Math.max(
                          128,
                          Math.min(
                            4096,
                            currentDimension.width % 2 === 0
                              ? currentDimension.width
                              : currentDimension.width - 1
                          )
                        );
                        setCurrentDimension((prev) => ({
                          ...prev,
                          width: evenWidth,
                        }));
                      }
                    }}
                    inputProps={{
                      min: 128,
                      max: 4096,
                      step: 2,
                    }}
                    variant="outlined"
                    size="small"
                    sx={{ width: "120px" }}
                  />
                  <Typography variant="body1">×</Typography>
                  <WhiteTextField
                    label="Height"
                    type="number"
                    value={currentDimension.height}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        setCurrentDimension((prev) => ({ ...prev, height: 0 }));
                        return;
                      }
                      const height = parseInt(value, 10);
                      if (!isNaN(height)) {
                        setCurrentDimension((prev) => ({ ...prev, height }));
                      }
                    }}
                    onBlur={() => {
                      if (currentDimension.height % 2 !== 0) {
                        const evenHeight = Math.max(
                          128,
                          Math.min(
                            4096,
                            currentDimension.height % 2 === 0
                              ? currentDimension.height
                              : currentDimension.height - 1
                          )
                        );
                        setCurrentDimension((prev) => ({
                          ...prev,
                          height: evenHeight,
                        }));
                      }
                    }}
                    inputProps={{
                      min: 128,
                      max: 4096,
                      step: 2,
                    }}
                    variant="outlined"
                    size="small"
                    sx={{ width: "120px" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{
            color: "white",
            "&:hover": {
              color: THEME_COLORS.yellow,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          sx={{
            backgroundColor: THEME_COLORS.yellow,
            color: "black",
            "&:hover": {
              backgroundColor: THEME_COLORS.darkYellow,
            },
          }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BackgroundSettingsDialog;
